//Components
import Module from '../../../../layout/Module';
import Box from '@mui/material/Box';

//Other dependencies
import { useEffect, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { loadSurveyPreview } from '../../../../../store/actions/dataEntrySurveyPreviewActions';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import AlertSaveButton from '../../../../button/AlertSaveButton';
import ButtonStartIcon from '../../../../button/ButtonStartIcon';
import TableSelective from './TableSelective';
import TableAbout from './TableAbout';
import CardKeyValue from '../../../../card/cardKeyValue';
import useDate from '../../../../../hooks/app/useDate';
import SurveyTemplateDialog from '../../components/SurveyTemplateDialog/SurveyTemplateDialog';
import Constraints from '../Constraints/Constraints';
import { SURVEY_TYPE_ABOUT_USERS, SURVEY_TYPE_STANDARD } from '../../constants/typeSurvey';
import useFetchData from '../../../../../hooks/useFetchData';
import SurveyStatisticsModal from '../SurveyStatistics/SurveyStatisticsModal';

const SurveyPreview = () => {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const { formatDate } = useDate();

  const dispatch = useDispatch();
  const currentModule = 'dataentrysurveys';
  const loading = useSelector((state) => state.dataEntrySurveyPreview?.surveyPreview?.loading ?? false);
  const data = useSelector((state) => state?.dataEntrySurveyPreview?.surveyPreview ?? {});
  const template = useSelector((state) => state.dataEntrySurveyPreview?.surveyPreview?.template ?? {});
  const { name } = data;
  const [filter, setFilter] = useState({});
  const [selectedRowResult, setSelectedRowResult] = useState(null);
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  const { isLoading: isLoadingYears, data: dataYears, error: errorFetchYears } = useFetchData('setting/year');

  const getSurveyCompleted = () => {
    if (data) {
      const { percentageCompleted, totalUsers, totalCompleted } = data;
      return `${
        !isNaN(percentageCompleted) ? percentageCompleted : '0.00'
      }% (${totalCompleted}/${totalUsers})`;
    }
    return '0.00% (0/0)';
  };

  const onPreview = async (row) => {
    const resultData = {
      ...template,
      questions: template?.questions?.map((question) => {
        const answer = row?.result?.answers?.find((item) => item?.questionId === question?.id) || [];
        return { ...question, answer: answer?.answer || [] };
      }),
      error: null
    };

    setSelectedRowResult(resultData);
  };
  const onLoadData = useCallback(() => {
    dispatch(loadSurveyPreview(surveyId));
  }, [surveyId]);

  useEffect(() => {
    onLoadData();
  }, [onLoadData]);

  return (
    <Module>
      <Module.Header
        moduleName={currentModule}
        title={name || 'Survey Preview'}
        titleLink={`/dashboard/data-entry/surveys/${surveyId}preview`}
      />
      <Module.BodyHeader py='4' className='border-b border-b-2 border-primary relative '>
        <Box className='flex w-full items-center gap-4'>
          <Box
            style={{ width: '10%' }}
            className='flex items-center justify-center gap-2 min-w-100px max-w-100px'
          >
            {loading ? (
              <Typography color={'primary'} className='text-center'>
                Survey
              </Typography>
            ) : data?.type === SURVEY_TYPE_STANDARD ? (
              <Typography color={'primary'} className='text-center'>
                Standard survey
              </Typography>
            ) : (
              <Typography color={'primary'} className='text-right'>
                Survey about {data?.type?.replace('about-', '')}
              </Typography>
            )}
          </Box>
          <Box className='flex items-center justify-center gap-2 '>
            <CardKeyValue
              className='min-w-200px max-w-150px '
              title='Template'
              lines={1}
              loading={loading}
              iconClassName='text-2xl'
              value={data?.template?.title || ''}
            />
            <CardKeyValue
              className='min-w-200px max-w-200px'
              title='Name'
              lines={1}
              value={data?.name || ''}
              loading={loading}
            />
            <CardKeyValue
              className='min-w-100px max-w-100px'
              title='Due date'
              lines={1}
              value={data?.dueDate ? formatDate(data?.dueDate) || '' : ''}
              loading={loading}
            />
            {data?.type === SURVEY_TYPE_STANDARD && (
              <CardKeyValue
                loading={loading}
                className='min-w-150px max-w-150px'
                title='Completed'
                lines={1}
                tooltip={false}
                icon={['far', 'square-poll-vertical']}
                value={getSurveyCompleted()}
              />
            )}

            {!loading && [...SURVEY_TYPE_ABOUT_USERS].includes(data?.type) && (
              <Constraints
                disabled={loading}
                isPreview={true}
                loading={loading}
                value={data?.data?.constraints?.rolesId?.[0] || ''}
              />
            )}
          </Box>
        </Box>
        <Box className='flex items-center gap-2 justify-end'>
          <ButtonStartIcon
            disabled={loading}
            onClick={() => setShowStatisticsModal(true)}
            startIcon={['fal', 'chart-bar']}
            title='Cancel'
            color='primary'
            variant='outlined'
            className='shadow-sm'
            style={{ width: '145px' }}
          >
            View Statistics
          </ButtonStartIcon>
          <AlertSaveButton
            submodule='Surveys'
            disabled={loading}
            icon={['fal', 'times']}
            title='Cancel'
            color='primary'
            variant='outlined'
            className='shadow-sm'
            onDontSave={() => navigate('/dashboard/data-entry/surveys')}
          />
        </Box>
      </Module.BodyHeader>
      <Module.BodyContainer>
        <div className='absolute inset-0 bottom-8'>
          {data?.type === SURVEY_TYPE_STANDARD ? (
            <TableSelective
              type={data?.type}
              disabled={loading || isLoadingYears}
              filter={filter}
              onFilter={(name, value) => setFilter((old) => ({ ...old, [name]: value }))}
              onPreview={onPreview}
              dataYears={dataYears}
            />
          ) : (
            <TableAbout
              type={data?.type}
              disabled={loading}
              filter={filter}
              onFilter={(name, value) => setFilter((old) => ({ ...old, [name]: value }))}
              dataYears={dataYears}
            />
          )}
        </div>
        {selectedRowResult && (
          <SurveyTemplateDialog
            id={`template-survey-list-modal`}
            open={!!selectedRowResult}
            onCancel={() => setSelectedRowResult(false)}
            onConfirm={() => setSelectedRowResult(false)}
            loading={loading}
            title={data?.template?.title || 'Template Preview'}
            onCloseHeader={true}
            maxWidth='lg'
            showActions={false}
            questions={selectedRowResult?.questions ?? []}
          />
        )}
      </Module.BodyContainer>
      <SurveyStatisticsModal
        open={showStatisticsModal}
        onClose={() => {
          setShowStatisticsModal(false);
        }}
        surveyId={data?._id}
        surveyName={data?.name}
        type={data?.type}
      />
    </Module>
  );
};
export default SurveyPreview;
